import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';
import { useCallback } from 'react';

import Dialog from '~components/Dialog/Dialog';
import DialogActions from '~components/Dialog/DialogActions';
import DialogContent from '~components/Dialog/DialogContent';
import DialogHeader from '~components/Dialog/DialogHeader';
import {
  SmallButton,
  SmallLoadingButton,
} from '~components/Order/ordersDispatchStyledComponents';

interface SettlementReExportConfirmationDialogProps {
  alreadyExportedSettlementIds: string[];
  isOpen: boolean;
  isSubmitting: boolean;
  onClose: () => void;
  onConfirm: () => void;
  rendererLabel: string;
}

export default function SettlementReExportConfirmationDialog({
  alreadyExportedSettlementIds,
  isOpen,
  isSubmitting,
  onClose,
  onConfirm,
  rendererLabel,
}: SettlementReExportConfirmationDialogProps) {
  const handleClose = useCallback(() => {
    if (isSubmitting) {
      return;
    }

    onClose();
  }, [isSubmitting, onClose]);

  const hasAlreadyExportedSettlements = alreadyExportedSettlementIds.length > 0;

  return (
    <Dialog isOpen={isOpen} maxWidth="xs" onClose={handleClose}>
      <DialogHeader onClose={handleClose}>
        {t('approvals.driver_pay.settlements.settlement_export.re_export_dialog.title', {
          rendererLabel,
        })}
      </DialogHeader>

      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {hasAlreadyExportedSettlements && (
            <Alert
              data-test-id="settlement-re-export-warning"
              severity="warning"
              sx={{ py: 0.5 }}
            >
              <Typography variant="body2">
                {t(
                  'approvals.driver_pay.settlements.settlement_export.re_export_dialog.already_exported_warning',
                  { count: alreadyExportedSettlementIds.length },
                )}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.5 }}>
                {alreadyExportedSettlementIds.join(', ')}
              </Typography>
            </Alert>
          )}

          <Typography variant="body2">
            {t(
              'approvals.driver_pay.settlements.settlement_export.re_export_dialog.confirmation',
            )}
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions>
        <SmallButton disabled={isSubmitting} onClick={handleClose} variant="outlined">
          {t('actions.cancel')}
        </SmallButton>
        <SmallLoadingButton
          data-test-id="settlement-re-export-confirm-button"
          loading={isSubmitting}
          onClick={onConfirm}
        >
          {t(
            'approvals.driver_pay.settlements.settlement_export.re_export_dialog.confirm',
          )}
        </SmallLoadingButton>
      </DialogActions>
    </Dialog>
  );
}
